import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { StatGrid } from "@/components/StatGrid";
import { PageHead } from "@/components/PageHead";
import { POLL } from "@/lib/format";

import { fota } from "@/api/client";
import { useCompanyScope } from "@/auth/companyScope";
import { Donut } from "@/components/Donut";
import { Field } from "@/components/Field";
import { Select } from "@/components/inputs";
import { kindLabel, useModelPool, useTemplates } from "@/modules/asset_catalog/common";

const VER_COLORS = ["var(--teal)", "var(--info)", "var(--warn)", "var(--t-dim)", "var(--line2)"];

/** Live fleet firmware-version distribution (OFT-07), sourced from
 *  device_fota_state — the version each device last reported over health. */
export function FotaOverviewPage() {
  // THE SAME JOURNEY AS A ROLLOUT, and deliberately the same vocabulary.
  // Dattatray: *"Overview page should ask user to select company, product --
  // that product will have models and those models will have different
  // controllers like Host / BLE -- and then we give the software
  // distribution. Right now it shows overall status of the devices without
  // these filters."*
  //
  // Company -> kind -> model -> controller is exactly what the Update page
  // asks, in the same order, from the same two sources (the registry's
  // companies and the Asset Catalog's active FOTA-capable models). The
  // distribution a person reads here and the rollout they build next are then
  // describing the same set, because they are literally the same filters --
  // the server reuses `_candidate_conds` for both.
  const { company } = useCompanyScope();   // the app bar's (D-134)
  const [kind, setKind] = useState("");
  const [modelId, setModelId] = useState("");
  const [method, setMethod] = useState("");

  const tpl = useTemplates();
  const pool = useModelPool();

  // Only models that DECLARE a FOTA route can have a version spread worth
  // charting, which is the same filter the rollout picker applies.
  const fotaModels = (pool.data?.models ?? []).filter((m) => m.status === "active"
    && Array.isArray((m.derived as { fota_methods?: unknown[] }).fota_methods)
    && ((m.derived as { fota_methods?: unknown[] }).fota_methods?.length ?? 0) > 0);
  const kinds = [...new Set(fotaModels.map((m) => m.kind))];
  const kindModels = fotaModels.filter((m) => !kind || m.kind === kind);
  const model = fotaModels.find((m) => m.version_id === modelId);
  type MethodTick = { key: string; software: string; route: string };
  const methods: MethodTick[] = ((model?.derived as { fota_methods?: MethodTick[] })?.fota_methods) ?? [];

  const dist = useQuery({
    queryKey: ["fota", "dist", company, modelId, method],
    queryFn: () => fota.fwDistribution({
      company: company || undefined,
      version_id: modelId || undefined,
      method: method || undefined,
    }),
    refetchInterval: POLL.DASHBOARD });
  const slices = dist.data?.slices ?? [];
  const total = slices.reduce((a, s) => a + s.count, 0);

  const bare = slices.filter((s) => !s.app_ver).reduce((a, s) => a + s.count, 0);
  const versions = slices.filter((s) => s.app_ver);
  const latest = versions.length ? Math.max(...versions.map((s) => s.app_ver ?? 0)) : null;
  const onLatest = versions.filter((s) => s.app_ver === latest).reduce((a, s) => a + s.count, 0);
  const behind = total - bare - onLatest;

  return (
    <div className="mpage on">
      <PageHead eyebrow="FOTA · Overview" title="Firmware across the fleet" />

      <div className="section-label">What to look at</div>
      <div className="panel panel-pad filterrow" style={{ marginBottom: 14 }}>
        <Field label="Product">
          <Select className="fill" value={kind}
            onChange={(e) => { setKind(e.target.value); setModelId(""); setMethod(""); }}>
            <option value="">{kinds.length ? "All products" : "no model declares FOTA"}</option>
            {kinds.map((k) => <option key={k} value={k}>{kindLabel(tpl.data, k)}</option>)}
          </Select>
        </Field>
        <Field label="Model">
          <Select className="fill" value={modelId} disabled={!kind}
            onChange={(e) => { setModelId(e.target.value); setMethod(""); }}>
            <option value="">{kind ? "All models" : "product first"}</option>
            {kindModels.map((m) => (
              <option key={m.version_id} value={m.version_id}>{m.name} · {m.manufacturer}</option>
            ))}
          </Select>
        </Field>
        <Field label="Controller">
          <Select className="fill" value={method} disabled={!modelId}
            onChange={(e) => setMethod(e.target.value)}>
            <option value="">{modelId ? "Host (default)" : "model first"}</option>
            {methods.map((m) => (
              <option key={m.key} value={m.key}>{m.software} - via {m.route}</option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="section-label">Fleet</div>
      <StatGrid stats={[
        { k: "Devices reporting", v: total, s: "seen via health check-ins" },
        { k: `On latest ${latest ? `· v${latest}` : ""}`, v: onLatest, s: `${total ? Math.round((onLatest / total) * 100) : 0}% of fleet` },
        { k: "Behind", v: behind, s: "candidates for a rollout" },
        { k: "Bare / no app", v: bare, s: "awaiting first install" },
      ]} />

      {/* The heading used to read "telematics devices" unconditionally. Once the
          controller is selectable that is only true for the default, and a
          chart titled for the wrong node is worse than an untitled one. */}
      <div className="section-label">
        Software version distribution{method && methods.find((m) => m.key === method)
          ? ` - ${methods.find((m) => m.key === method)?.software}`
          : " - host controller"}
      </div>
      <div className="chartgrid">
        <div className="panel chart">
          <div className="ct">Devices by firmware version</div>
          <Donut
            slices={slices.map((s, i) => ({
              label: s.app_ver ? `v${s.app_ver}` : "none / bare",
              value: s.count,
              color: s.app_ver ? VER_COLORS[i % VER_COLORS.length] : "var(--warn)",
              display: `${s.count.toLocaleString("en-IN")} dev`,
            }))}
          />
        </div>
      </div>
    </div>
  );
}
